import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Form, Button, InputGroup } from "react-bootstrap";
import { useModuleContext } from "../../Context/ModuleContext";
import { useAuth } from "../../Context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHttpClient } from "../../hooks/http-hook";
import { useTranslation } from "react-i18next"; // Importer useTranslation
import {
  faFolderOpen,
  faFolder,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faFile,
  faBook,
  faLightbulb,
  faQuestionCircle,
  faCloudUploadAlt,
  faFilePdf,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";

const ModuleSidebar = () => {
  const location = useLocation();
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const comeFromSection = location.state?.comeFromSection || null;
  const selectedLessonFromState = location.state?.selectedLesson || null;
  const [error, setError] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(
    selectedLessonFromState || ""
  );
  const [expandedSections, setExpandedSections] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const sidebarRef = useRef(null);
  const { sendRequest } = useHttpClient();
  const { moduleId, id } = useParams();
  const navigate = useNavigate();
  const { module, loading } = useModuleContext();
  const [userProgress, setUserProgress] = useState(null);
  const { authData } = useAuth();

  // Fetch module data
  useEffect(() => {
    if (module && module.sections) {
      const sectionsState = {};
      module.sections.forEach((section) => {
        sectionsState[section.id] = comeFromSection === section.id;
      });
      setExpandedSections(sectionsState);
    }
  }, [module, comeFromSection]);

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const lessonIdFromUrl = pathParts[pathParts.length - 1];

    if (module && module.sections) {
      let updatedSections = {};
      let foundLesson = false;

      module.sections.forEach((section) => {
        const hasLesson = section.lessons.some(
          (lesson) => lesson.id === lessonIdFromUrl
        );
        if (hasLesson) {
          updatedSections[section.id] = true;
          setSelectedLesson(lessonIdFromUrl); // Sélectionne la leçon
          foundLesson = true;
        } else {
          updatedSections[section.id] = false;
        }
      });

      if (foundLesson) {
        setExpandedSections(updatedSections);
      }
    }
  }, [location, module]);

  // fetch user progress
  useEffect(() => {
    const fetchUserProgress = async () => {
      try {
        if (!module || !module.program) {
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}user-program-progress/get?program=${module.program}&user=${authData.userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(t("error_fetch_user_progress"));
        }

        const data = await response.json();
        setUserProgress(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchUserProgress();
  }, [module, authData.userId]);

  // Gère la complétion de la leçon
  const handleLessonCompletion = async (lessonId, isCompleted, sectionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}user-program-progress/${lessonId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            completed: isCompleted,
            user: authData.userId,
            program: module.program,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        console.log(data.message);
      }

      // Mettre a jour l'etat local
      setUserProgress((prev) => {
        if (!prev) return prev;

        let updatedLessons = [...prev.completedLessons];

        if (isCompleted) {
          updatedLessons.push(lessonId);
        } else {
          updatedLessons = updatedLessons.filter((id) => id !== lessonId);
        }

        // Regarde si une lecon est completee maintenant
        const section = module.sections.find((s) => s.id === sectionId);
        const visibleLessons = section.lessons.filter(
          (l) => l.is_visible === true && l.is_deleted === false
        );
        const allLessonsCompleted = visibleLessons.every((id) =>
          id === lessonId ? isCompleted : updatedLessons.includes(id)
        );

        let updatedSections = [...prev.completedSections];

        // Mettre a jour les sections completes
        if (allLessonsCompleted && !updatedSections.includes(sectionId)) {
          updatedSections.push(sectionId);
        } else if (
          !allLessonsCompleted &&
          updatedSections.includes(sectionId)
        ) {
          updatedSections = updatedSections.filter((id) => id !== sectionId);
        }

        return {
          ...prev,
          completedLessons: updatedLessons,
          completedSections: updatedSections,
        };
      });
    } catch (error) {
      setError(error.message);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return userProgress?.completedLessons.includes(lessonId) || false;
  };

  const getSectionProgress = (sectionId) => {
    if (!userProgress || !module) return { completed: 0, total: 0 };

    const section = module.sections.find((s) => s.id === sectionId);
    if (!section) return { completed: 0, total: 0 };

    const visibleLessons = section.lessons.filter(
      (l) => l.is_visible === true && l.is_deleted === false
    );

    const total = visibleLessons.length;

    // Convert IDs to strings for comparison
    const completed = visibleLessons.filter((l) =>
      userProgress.completedLessons.some(
        (id) => id === l.id || id === l.id.toString()
      )
    ).length;

    return { completed, total };
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Change l'état de la barre latérale
  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);

    if (!collapsed === true) {
      const allClosed = {};
      module.sections.forEach((section) => {
        allClosed[section.id] = false;
      });
      setExpandedSections(allClosed);
    } else {
      // Ensure comeFromSection is expanded when sidebar is expanded
      if (comeFromSection) {
        setExpandedSections((prev) => ({
          ...prev,
          [comeFromSection]: true,
        }));
      }
    }

    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 300);
  };

  // Retourne un tableau de sections filtrées
  // Si le module ou les sections n'existent pas, elle retourne un tableau vide
  // Si le terme de recherche n'existe pas, elle retourne toutes les sections visibles
  const getFilteredSections = () => {
    if (!module || !module.sections) return [];

    // First filter by visibility
    const visibleSections = module.sections.filter(
      (section) => section.is_visible
    );

    // Then filter by search term if needed
    if (!searchTerm) return visibleSections;

    return visibleSections
      .map((section) => {
        // Filter lessons that match search AND are visible
        const filteredLessons = section.lessons
          .filter((lesson) => lesson.is_visible)
          .filter((lesson) =>
            lesson.title.toLowerCase().includes(searchTerm.toLowerCase())
          );

        if (
          filteredLessons.length > 0 ||
          section.title.toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          return {
            ...section,
            lessons: filteredLessons,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredSections = getFilteredSections();

  // Gère le changement de la barre de recherche
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value) {
      const newExpandedSections = { ...expandedSections };
      filteredSections.forEach((section) => {
        newExpandedSections[section.id] = true;
      });
      setExpandedSections(newExpandedSections);
    }
  };

  const findCurrentLessonIndex = () => {
    for (let i = 0; i < module.sections.length; i++) {
      const section = module.sections[i];
      const lessonIndex = section.lessons.findIndex(
        (lesson) => lesson.id === selectedLesson
      );
      if (lessonIndex !== -1) {
        return { sectionIndex: i, lessonIndex };
      }
    }
    return { sectionIndex: -1, lessonIndex: -1 };
  };

  if (loading) return <div className="p-4">L{t("loading_module")}</div>;
  if (error)
    return (
      <div className="p-4 text-danger">
        {t("error")} {error}
      </div>
    );
  if (!module) return <div className="p-4">{t("no_module_found")}</div>;

  const getIconForLessonType = (type) => {
    switch (type) {
      case "Theory":
        return faBook;
      case "Quiz":
        return faQuestionCircle;
      case "Upload":
        return faCloudUploadAlt;
      case "PDF":
        return faFilePdf;
      case "Word":
        return faFileWord;
      default:
        return null;
    }
  };

  const getColorForLessonType = (type) => {
    switch (type) {
      case "Theory":
        return "#0d6efd";
      case "Quiz":
        return "#28a745";
      case "Upload":
        return "#17a2b8";
      case "PDF":
        return "#e60000";
      case "Word":
        return "#1e3799";
      default:
        return "#6c757d";
    }
  };

  return (
    <div
      className="bg-light border-end"
      ref={sidebarRef}
      style={{
        width: collapsed ? "60px" : "280px",
        transition: "width 0.3s ease",
      }}
    >
      {/* Sidebar header */}
      <div className="sidebar-header p-2 border-bottom d-flex justify-content-between align-items-center">
        {!collapsed && (
          <Form className="w-100 me-2">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} className="text-primary" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t("search_placeholder")}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Form>
        )}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={toggleSidebar}
          className={collapsed ? "mx-auto" : ""}
        >
          <FontAwesomeIcon
            icon={collapsed ? faChevronRight : faChevronLeft}
            className="text-dark"
          />
        </Button>
      </div>

      {/* Sidebar body */}
      <div className="sidebar-body p-2">
        {filteredSections.map((section) => {
          const { completed, total } = getSectionProgress(section.id);

          return (
            <div key={section.id} className="mb-3">
              <div
                className="section-header d-flex align-items-center p-2 rounded cursor-pointer"
                onClick={() => toggleSection(section.id)}
                style={{
                  cursor: "pointer",
                  backgroundColor: expandedSections[section.id]
                    ? "#e9ecef"
                    : "transparent",
                }}
              >
                <FontAwesomeIcon
                  icon={expandedSections[section.id] ? faFolderOpen : faFolder}
                  className="text-primary"
                />
                {!collapsed && (
                  <div className="d-flex justify-content-between align-items-center w-100 ms-2">
                    <span className="fw-bold text-truncate">
                      {section.title}
                    </span>
                    <span
                      className={`badge ${
                        completed === total ? "bg-success" : "bg-secondary"
                      } ms-2`}
                    >
                      {completed}/{total}
                    </span>
                  </div>
                )}
              </div>

              {/* Lessons list */}
              {expandedSections[section.id] && !collapsed && (
                <div className="lessons-list ms-4 mt-2">
                  {section.lessons
                    .filter((lesson) => lesson.is_visible) // Montre uniquement les leçons visibles
                    .map((lesson) => {
                      const isCompleted = isLessonCompleted(lesson.id);

                      return (
                        <div
                          key={lesson.id}
                          onClick={() => {
                            navigate(
                              `/programs/${id}/module/${moduleId}/${lesson.id}`
                            );
                            setSelectedLesson(lesson.id);
                          }}
                          className="lesson-item p-2 rounded d-flex align-items-center"
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedLesson === lesson.id
                                ? "#e9ecef"
                                : "transparent",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={getIconForLessonType(lesson.type)}
                            className="me-2"
                            style={{
                              color: getColorForLessonType(lesson.type),
                            }}
                          />
                          <span className="text-truncate">
                            {lesson.title}
                            {lesson.type === "Quiz" && (
                              <small className="ms-1 text-muted">
                                (Auto-completion)
                              </small>
                            )}
                          </span>
                          <div className="ms-auto">
                            <Form.Check
                              type="checkbox"
                              id={`lesson-check-${lesson.id}`}
                              checked={isCompleted}
                              onChange={(e) =>
                                handleLessonCompletion(
                                  lesson.id,
                                  e.target.checked,
                                  section.id
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="me-auto"
                              disabled={lesson.type === "Quiz"}
                            />
                          </div>
                        </div>
                      );
                    })}
                  {section.lessons.length === 0 && (
                    <div className="text-muted small fst-italic p-2">
                      {t("no_lesson_found")}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filteredSections.length === 0 && (
          <div className="text-center text-muted p-3">
            {t("unmatch_search")}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleSidebar;
